// React
import React, { FC, useEffect, useState } from "react";
import { setLocale } from "yup";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { compose, bindActionCreators, Dispatch } from "redux";
// Hooks
import { useFormatMessage } from "../../../../hooks/formatMessage";
import { useSwal } from "../../../../hooks/useSwal";
// Layout/Components
import { Flex } from "reflexbox";
import { Form } from "@components/Form";
import { FormInput as Input } from "@components/Form/Input";
import { FormSelect as Select } from "@components/Form/Select";
import { Button as OldButton, EButtonType } from "@components/OldButton";
import { Button } from "@components/Button";
import { ReactComponent as IconArrowRight } from "../../../../assets/icons/arrow-right.svg";
// Models
import { ACTION } from "../../../../shared/models/action";
// Actions
import { TProps } from "./types";
import { ObjectSchema } from "./schema";
import { createObject, changeObject } from "../ducks";
import { fetchObjectTypes, fetchApplications } from "../../../../actions/enumerator";

setLocale({
  mixed: {
    default: "app.requiredField",
    required: "app.requiredField",
  },
  string: {
    required: "app.requiredField",
  },
  number: {
    required: "app.requiredField",
  },
  object: {
    required: "app.requiredField",
  },
});

const ObjectsModal: FC<TProps> = props => {
  // Props
  const { closeModal, initialData, fetchApplications, fetchObjectTypes } = props;
  // Language
  const formatMessage = useFormatMessage();
  // Swal
  const { swalConfirmationMessage, swalAlertWithCallback } = useSwal();
  // Page control
  const [isEditing, setIsEditing] = useState<boolean>();
  // Lists
  const [objectTypesList, setObjectTypeList] = useState<any>();
  const [applicationsList, setApplicationsList] = useState<any>();

  useEffect(() => {
    setIsEditing(!!initialData.id);
  }, [initialData.id]);

  useEffect(() => {
    document.body.classList.remove("backdrop");
  }, []);

  // Fetch lists
  useEffect(() => {
    fetchObjectTypes().then(ret => setObjectTypeList(ret));
    fetchApplications().then(ret => setApplicationsList(ret));
  }, [fetchObjectTypes, fetchApplications]);

  const showSuccessMessage = action => {
    swalAlertWithCallback(
      {
        text: `Object has been ${action}!`,
        type: "success",
        confirmationText: "Ok!",
      },
      closeModal
    );
  };

  const onSubmit = formData => {
    const object = {
      ...formData,
      applicationCode: formData.applicationCode ? formData.applicationCode.value : undefined,
      objectType: formData.objectType ? formData.objectType.value : undefined,
    };

    if (isEditing) {
      swalConfirmationMessage({
        text: "Do you really want to change this object?",
        // content: (),
        type: "info",
        cancelationText: "Cancel",
        confirmationText: "Save changes",
      }).then(value => {
        if (value) {
          // const objectToEdit = Object.keys(object).map(key => {
          //   return {
          //     op: "replace",
          //     path: `/${[key]}`,
          //     value: object[key],
          //   };
          // });

          changeObject(formData.id, object).then(res => {
            if (res.success) showSuccessMessage("changed");
          });
        }
      });
    } else {
      delete object.id;
      swalConfirmationMessage({
        text: "Do you really want to create this object?",
        type: "info",
        cancelationText: "Cancel",
        confirmationText: "Save new Object",
      }).then(value => {
        if (value) {
          createObject(object).then(res => {
            if (res.success) showSuccessMessage("created");
          });
        }
      });
    }
  };

  return (
    <div className="modal">
      <div className="modal-header">
        <div className="modal-header-left">
          <OldButton
            className="primary"
            onClick={closeModal}
            to={null}
            buttonType={EButtonType.ButtonText}
          >
            <div className="iconWrapper arrow left d-inline-block mr-3 small">
              <IconArrowRight />
            </div>
            {formatMessage("app.backToOverview", "Zurück zur Übersicht")}
          </OldButton>
        </div>
      </div>
      <Flex flexDirection="column" mb={20}>
        <Form onSubmit={onSubmit} schema={ObjectSchema} defaultValues={initialData}>
          <Flex>
            <Input id="id" name="id" hidden />
            <Input
              id="objectName"
              name="objectName"
              label={formatMessage("app.objectName", "Object Name")}
            />
          </Flex>
          <Flex mt="30px">
            <Select
              id="applicationCode"
              name="applicationCode"
              label={formatMessage("app.applicationCode", "Application Code")}
              options={applicationsList?.map(option => ({
                value: option.id,
                label: option.description,
              }))}
            />
          </Flex>
          <Flex mt="30px">
            <Select
              id="objectType"
              name="objectType"
              label={formatMessage("app.objectType", "Object Type")}
              options={objectTypesList?.map(option => ({
                value: option.id,
                label: option.description,
              }))}
            />
          </Flex>
          <Flex mt={20} width="100%" justifyContent="flex-end" pr="15px">
            <Button id="ModalBtn_cancel" textButton mr="10px" onClick={closeModal}>
              {formatMessage("app.cancel", "Cancel")}
            </Button>
            <Button id="ModalBtn_save" type="submit">
              {formatMessage("app.save", "Save")}
            </Button>
          </Flex>
        </Form>
      </Flex>
    </div>
  );
};

function mapDispatchToProps(dispatch: Dispatch<ACTION>) {
  return {
    fetchObjectTypes: bindActionCreators(fetchObjectTypes, dispatch),
    fetchApplications: bindActionCreators(fetchApplications, dispatch),
  };
}

const enhance = compose(connect(null, mapDispatchToProps), injectIntl);

export default enhance(ObjectsModal);
